import type { Category } from './types';

export interface CategoryHelp {
  what: string;
  why: string;
}

/** Plain-language help for each Readiness category (the (i) expander). */
export const CATEGORY_HELP: Record<Category, CategoryHelp> = {
  crawlability: {
    what: 'Can AI crawlers even reach and read your content? Covers AI-bot access in robots.txt (GPTBot, ClaudeBot, PerplexityBot, Google-Extended…), whether content is in the HTML or hidden behind JavaScript, sitemap and HTTP status.',
    why: 'This is a gate, not a normal category: if AI bots are blocked or your content only renders in the browser, nothing else matters. AI never sees the page, so it can never cite you. A blocked bot caps your whole score.',
  },
  structure: {
    what: 'Can AI break your page into clean, quotable chunks? A single clear H1, semantic HTML (article/section/lists/tables), answer-first phrasing, concrete facts and dates.',
    why: 'This is the ONLY category with proven causal impact on AI citation (Princeton "GEO" experiments: adding quotes/statistics/sources lifted visibility 28 to 41%). It is also the part you most directly control by editing content.',
  },
  entity: {
    what: 'Is your brand an unambiguous entity AI can identify? Organization schema (JSON-LD), sameAs links to your official profiles (LinkedIn, Wikipedia, Crunchbase…), a clear title and meta description.',
    why: 'AI needs to be sure "this is that specific company" to recommend you confidently. Knowledge-base presence (Wikipedia/Wikidata) correlates strongly with being named; raw schema markup helps parsing but is weaker.',
  },
  citability: {
    what: 'When AI actually reads your page, can it cleanly state who you are and pull quotable facts? Measured by asking the model itself to extract entity, facts and a citation-ready answer.',
    why: 'A direct read of "if AI landed here, could it cite you?". It is a heuristic (the model\'s own judgement), so it is marked estimated, but it is the closest single test of on-page citability.',
  },
  offpage: {
    what: 'Your reputation OFF your own site: backlinks, brand mentions, presence in directories and knowledge bases. The on-page footprint (sameAs, KB links) is measured honestly and the rest is flagged as not measured.',
    why: 'This is the strongest correlate of AI citation in large studies (brand mentions ≈ 3× stronger than backlinks). But it lives off your site, so only part of it is visible from here. What cannot be measured is named as such rather than guessed.',
  },
};

export interface FindingHelp {
  /** substring matched against the finding title (case-insensitive) */
  match: string;
  what: string;
  how: string;
  tools?: { label: string; url: string }[];
}

/** What-it-is + how-to-fix + tools per finding type. Matched by title substring. */
export const FINDING_HELP: FindingHelp[] = [
  {
    match: 'sameAs',
    what: 'sameAs is a JSON-LD property that links your website to your official profiles elsewhere (LinkedIn, Crunchbase, Wikipedia, GitHub, X…). It tells search engines and AI "all of these are the same entity, us".',
    how: 'Add an Organization block in JSON-LD with a "sameAs" array of your verified profile URLs. Put it in the <head> of your homepage. Then verify it renders correctly.',
    tools: [
      { label: 'Schema.org Organization', url: 'https://schema.org/Organization' },
      { label: 'Google Rich Results Test', url: 'https://search.google.com/test/rich-results' },
    ],
  },
  {
    match: 'Organization schema',
    what: 'Organization schema (JSON-LD) is structured data that states your name, logo, URL and profiles in a machine-readable way, so AI can identify you as a real, specific entity.',
    how: 'Add a <script type="application/ld+json"> with @type "Organization" (name, url, logo, sameAs) to your homepage <head>. Validate it, then re-scan.',
    tools: [
      { label: 'Organization schema guide', url: 'https://developers.google.com/search/docs/appearance/structured-data/organization' },
      { label: 'Rich Results Test', url: 'https://search.google.com/test/rich-results' },
    ],
  },
  {
    match: 'knowledge-base presence',
    what: 'Knowledge bases (Wikipedia, Wikidata, Crunchbase) are where AI models "learn" that a brand exists. Being present there is one of the strongest signals that AI will name you unprompted.',
    how: 'If your brand meets notability criteria, create a Wikidata item (easiest, open) and a Crunchbase profile; Wikipedia only if you genuinely qualify. Then link them via sameAs so the connection is visible.',
    tools: [
      { label: 'Create a Wikidata item', url: 'https://www.wikidata.org/wiki/Special:NewItem' },
      { label: 'Crunchbase', url: 'https://www.crunchbase.com/' },
    ],
  },
  {
    match: 'Backlink & brand-mention',
    what: 'Backlinks and brand mentions are other sites linking to or talking about you. This off-site reputation is the single strongest correlate of AI citation, but it can only be measured with a paid backlink index, so it is reported as not measured rather than guessed.',
    how: 'Earn authoritative mentions: guest posts, integration/partner directories, industry roundups, press, and getting listed by the platforms you integrate with. Track progress with a dedicated backlink tool.',
    tools: [
      { label: 'Ahrefs', url: 'https://ahrefs.com/' },
      { label: 'Semrush', url: 'https://www.semrush.com/' },
    ],
  },
  {
    match: 'llms.txt',
    what: 'llms.txt is a proposed file (like robots.txt, but for LLMs) that tells AI what your site is about. Honest note: adoption by real AI crawlers is currently ~0. It is hygiene, not a ranking factor.',
    how: 'Optional: add a /llms.txt with a short description and key links. Do NOT prioritise it over content and structure. Those actually move the needle.',
    tools: [{ label: 'llms.txt spec', url: 'https://llmstxt.org/' }],
  },
  {
    match: 'H1',
    what: 'The H1 is the main heading of the page. AI (and search engines) use it as the primary signal of what the page is about. One clear H1 per page.',
    how: 'Add exactly one <h1> stating the page\'s topic in plain words; demote any extra H1s to H2/H3. Keep it descriptive, not a slogan.',
  },
  {
    match: 'non-semantic structure',
    what: 'Semantic HTML (article, section, lists, tables) exposes the structure of your content so AI can split it into citable units. A flat wall of <div>s is hard to chunk.',
    how: 'Wrap content in <article>/<section>, use <ul>/<ol> for lists and <table> for comparisons. Break long text into headed sections with answer-first paragraphs.',
  },
  {
    match: 'fact density',
    what: 'Fact/statistic density is how many concrete numbers, dates and figures your content contains. Controlled research found adding statistics lifts AI citation ~+33%.',
    how: 'Add specific, truthful numbers: results, capacities, dates, percentages, counts. AI quotes concrete facts far more readily than vague claims.',
  },
  {
    match: 'freshness',
    what: 'Freshness signals are published/updated dates in your markup. Some engines favour recent, clearly-dated content.',
    how: 'Expose datePublished/dateModified in JSON-LD (Article) and a visible date, and keep key pages updated.',
  },
  {
    match: 'meta description',
    what: 'The meta description is a short summary of the page. It helps AI and search engines grasp the page at a glance.',
    how: 'Add a concise, specific <meta name="description"> (~150 chars) summarising the page.',
  },
  {
    match: 'client-side JS',
    what: 'If your key content only appears after JavaScript runs, many AI crawlers (which often don\'t execute JS) see an almost-empty page. So they can\'t read or cite you.',
    how: 'Server-render or pre-render the important content so it is present in the initial HTML. Test by viewing the page source (not the inspector).',
  },
  {
    match: 'AI crawler blocked',
    what: 'Your robots.txt disallows one or more of the AI crawlers that actually cite you (GPTBot, ClaudeBot, PerplexityBot, Google-Extended). Blocked bot = you can never be cited by that engine.',
    how: 'Edit robots.txt to Allow these user-agents (or remove the Disallow). Many sites block them by accident via a broad rule.',
    tools: [{ label: 'robots.txt basics', url: 'https://developers.google.com/search/docs/crawling-indexing/robots/intro' }],
  },
  {
    match: 'sitemap',
    what: 'A sitemap lists your pages so crawlers can discover them. Helps AI find everything worth citing.',
    how: 'Publish /sitemap.xml and reference it in robots.txt with a Sitemap: line.',
  },
  {
    match: 'identify who/what you are',
    what: 'When AI read your page, it could not confidently say who you are and what you do. The entity is unclear.',
    how: 'State plainly, above the fold, who you are and what you do (one sentence). Back it with Organization schema and sameAs.',
  },
  {
    match: 'compose a citable answer',
    what: 'AI judged it could not build an answer and cite this page. The content isn\'t answer-shaped or lacks quotable facts.',
    how: 'Add answer-first content: pose the buyer\'s question as a heading and answer it directly with concrete, quotable facts.',
  },
  {
    match: 'confused with others',
    what: 'AI wasn\'t sure your brand is unambiguous. The name may collide with other companies, so it hesitates to recommend you.',
    how: 'Strengthen entity grounding: Organization schema, sameAs to authoritative profiles, distinctive consistent naming, and knowledge-base presence.',
  },
];

export function findingHelpFor(title: string): FindingHelp | undefined {
  const t = title.toLowerCase();
  return FINDING_HELP.find((h) => t.includes(h.match.toLowerCase()));
}
